import { t } from 'ember-intl';
import { and, eq, or } from 'ember-truth-helpers';
import { variation } from 'ember-launch-darkly';
import {
  HdsIcon,
  HdsBadge,
  HdsTextBody,
  HdsTooltipButton,
  HdsCopyButton,
} from '@hashicorp/design-system-components/components';
import { Flex, Time, CoreCardSummary } from 'core/components';
import { optionForSourceState } from 'core/helpers';

const SummaryCard = <template>
  <CoreCardSummary
    @type="horizontal"
    data-test-group-detail-summary-card
    as |Card|
  >
    <Card.Pair as |P|>
      <P.Key
        data-test-group-detail-summary-card-key
        data-test-group-detail-summary-card-key-type
      >
        {{t "components.iam.groups.summary-card.principal-type"}}
      </P.Key>
      <P.Value
        data-test-group-detail-summary-card-value
        data-test-group-detail-summary-card-value-type
      >
        {{#let (optionForSourceState "policy-type" "group") as |option|}}
          <Flex @alignItems="center" @direction="row" @gap="xs">
            <span>
              <HdsIcon @name={{option.icon}} @isInline={{true}} />
            </span>
            <span>
              {{t option.text}}
            </span>
          </Flex>
        {{/let}}
      </P.Value>
    </Card.Pair>
    <Card.Pair as |P|>
      <P.Key
        data-test-group-detail-summary-card-key
        data-test-group-detail-summary-card-key-members
      >
        {{t "components.iam.groups.summary-card.members"}}
      </P.Key>
      <P.Value
        data-test-group-detail-summary-card-value
        data-test-group-detail-summary-card-value-members
      >
        {{@group.memberCount}}
      </P.Value>
    </Card.Pair>
    {{#if (and (eq @ssoEnabled true) (variation "hcp-identity-scim-enabled"))}}
      <Card.Pair as |P|>
        <P.Key
          data-test-group-detail-summary-card-key
          data-test-group-detail-summary-card-key-scim-provisioning
        >
          {{t "components.iam.groups.summary-card.scim-provisioning.title"}}
        </P.Key>
        <P.Value
          data-test-group-detail-summary-card-value
          data-test-group-detail-summary-card-value-scim-provisioning
        >
          {{#let
            (and @scimEnabled (eq @group.scimSynchronized true))
            (eq @scimEnabled false)
            as |syncing notEnabled|
          }}
            {{#if (or syncing notEnabled)}}
              <HdsBadge
                @color={{if syncing "success"}}
                @icon={{if syncing "check" "x"}}
                @text={{if
                  syncing
                  (t
                    "components.iam.groups.summary-card.scim-provisioning.syncing"
                  )
                  (t
                    "components.iam.groups.summary-card.scim-provisioning.not-enabled"
                  )
                }}
                @type="outlined"
                data-test-group-detail-scim-provisioning-badge
              />
            {{else}}
              <Flex @alignItems="center" @direction="row" @gap="sm">
                <HdsTextBody
                  @color="faint"
                  @size="200"
                  @weight="regular"
                  data-test-group-detail-scim-provisioning-created-in-hcp
                >
                  {{t
                    "components.iam.groups.summary-card.scim-provisioning.created-in-hcp"
                  }}
                </HdsTextBody>
                <HdsTooltipButton
                  @text={{t
                    "components.iam.groups.summary-card.scim-provisioning.created-in-hcp-tooltip"
                  }}
                  @placement="bottom"
                  aria-label={{t
                    "components.iam.groups.summary-card.scim-provisioning.created-in-hcp-tooltip"
                  }}
                  data-test-group-detail-scim-provisioning-created-in-hcp-tooltip
                >
                  <HdsIcon
                    @name="alert-circle"
                    @color="var(--token-color-foreground-faint)"
                    @isInline={{true}}
                  />
                </HdsTooltipButton>
              </Flex>
            {{/if}}
          {{/let}}
        </P.Value>
      </Card.Pair>
    {{/if}}
    <Card.Pair as |P|>
      <P.Key
        data-test-group-detail-summary-card-key
        data-test-group-detail-summary-card-key-created
      >
        {{t "components.iam.groups.summary-card.created"}}
      </P.Key>
      <P.Value
        data-test-group-detail-summary-card-value
        data-test-group-detail-summary-card-value-created
      >
        <Time @date={{@group.createdAt}} @display="friendly-only" />
      </P.Value>
    </Card.Pair>
    <Card.Pair as |P|>
      <P.Key
        data-test-group-detail-summary-card-key
        data-test-group-detail-summary-card-key-resource-name
      >
        {{t "components.iam.groups.summary-card.resource-name"}}
      </P.Key>
      <P.Value
        data-test-group-detail-summary-card-value
        data-test-group-detail-summary-card-value-resource-name
      >
        {{#if @group.resourceName}}
          <Flex @alignItems="center" @direction="row" @gap="2xs">
            {{@truncatedResourceName}}
            <HdsTooltipButton
              @text={{t
                "components.iam.groups.summary-card.tooltip.text"
                resourceName=@group.resourceName
                htmlSafe=true
              }}
              aria-label={{t
                "components.iam.groups.summary-card.tooltip.aria-label"
              }}
            >
              <HdsIcon @name="info" @isInline={{true}} />
            </HdsTooltipButton>
            <HdsCopyButton
              @isIconOnly={{true}}
              @size="small"
              @text={{t "components.iam.groups.summary-card.resource-name"}}
              @textToCopy={{@group.resourceName}}
            />
          </Flex>
        {{else}}
          —
        {{/if}}
      </P.Value>
    </Card.Pair>
  </CoreCardSummary>
</template>;

export default SummaryCard;
